import { Component, OnInit } from '@angular/core';
import { ChartConfiguration, ChartData, ChartType, Plugin } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { InvoiceApi } from '@app/services/invoice-api';
import { InvoiceInterface } from '@app/interface/invoice';
import { finalize } from 'rxjs/operators';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { AfterViewInit } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute } from '@angular/router';
import { RecursiveInterface } from '@app/services/base-authenticated.service';
import { Input, OnChanges, SimpleChanges } from '@angular/core';
import { CorrispettiviReceiptsApi } from '@app/services/corrispettivi-receipts-api.service';
import { CorrispettiviReceiptsInterface } from '@app/interface/corrispettivi-receipts';
import { forkJoin, lastValueFrom, Observable, of, Subscription } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import { OnDestroy } from '@angular/core';

// Limite di attesa per ciascuna delle due chiamate. getRecursivePage() accetta
// un parametro `timeout` ma non lo applica mai, quindi senza questo l'attesa e'
// potenzialmente infinita e lo spinner non si sblocca piu'.
const REQUEST_TIMEOUT_MS = 30000;

@Component({
  selector: 'app-bar-plot',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    BaseChartDirective,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
  templateUrl: './bar-plot.component.html',
  styleUrls: ['./bar-plot.component.scss'],
})
export class BarPlotComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() selectedYear: number = new Date().getFullYear();

  public barChartData: ChartData<'bar'> = {
    labels: ['Q1', 'Q2', 'Q3', 'Q4'],
    datasets: [],
  };

  public barChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#616161' },
      },
      y: {
        beginAtZero: true,
        grid: { color: 'rgba(0,0,0,0.1)' },
        ticks: {
          color: '#616161',
          callback: (value) => '€' + value,
        },
      },
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#424242',
          font: { family: '"Outfit", system-ui, sans-serif', weight: 'normal' },
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      tooltip: {
        backgroundColor: '#616161',
        titleColor: '#ffffff',
        bodyColor: '#ffffff',
        padding: 12,
        cornerRadius: 12,
        displayColors: true,
        callbacks: {
          label: (context) => {
            const datasetIndex = context.datasetIndex;
            const value = context.raw;

            // First dataset (index 0) is amount, second (index 1) is count
            if (datasetIndex === 0) {
              const numericValue = Number(value);
              if (!isNaN(numericValue)) {
                return `Totale: €${numericValue.toLocaleString('it-It', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`;
              } else {
                return `Totale: €0,00`;
              }
            } else if (datasetIndex === 1) {
              return `Numero documenti: ${value}`;
            }

            return `Valore: ${value}`;
          },
        },
      },
    },
  };

  public barChartType: ChartType = 'bar';
  public isLoading = true;
  public errorMessage: string | null = null;
  // Fallimento di UNA sola delle due sorgenti: il grafico si disegna comunque
  // con i dati disponibili e avvisa che è incompleto.
  public warningMessage: string | null = null;

  // Caricamento in corso: annullato prima di lanciarne un altro, così un
  // cambio anno / doppio click su "Ricarica dati" non lascia due risposte in
  // volo che si sovrascrivono a vicenda in ordine casuale.
  private dataSubscription?: Subscription;

  // Adds extra space strictly BELOW the legend (between the legend row and
  // the chart area), without affecting the space above it.
  public barChartPlugins: Plugin[] = [
    {
      id: 'legendBottomSpacing',
      beforeInit(chart) {
        const legend = chart.legend as any;
        if (legend && !legend.__originalFit) {
          legend.__originalFit = legend.fit;
          legend.fit = function () {
            legend.__originalFit.call(this);
            this.height += 24;
          };
        }
      },
    },
  ];

  constructor(
    private invoiceApi: InvoiceApi,
    private Corrispettiviservice: CorrispettiviReceiptsApi,
    private cdr: ChangeDetectorRef,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.loadInvoiceData();

    this.cdr.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // When selectedYear changes, reload the data
    if (changes['selectedYear'] && !changes['selectedYear'].firstChange) {
      this.loadInvoiceData();
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 100);

    this.cdr.detectChanges();
  }

  // ngOnChanges(): void {
  //   this.cdr.detectChanges();
  // }

  // now actually invoices and corrispettivi
  loadInvoiceData(): void {
    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    // Una sola richiesta alla volta: la precedente viene annullata.
    this.dataSubscription?.unsubscribe();

    this.isLoading = true;
    this.errorMessage = null;
    this.warningMessage = null;
    this.cdr.detectChanges();

    if (!Number.isFinite(organization_id) || organization_id <= 0) {
      console.error('bar-plot: organizationId mancante nella route');
      this.errorMessage = 'Errore nel caricamento dei dati.';
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // NB: getRecursivePage() muta l'oggetto params (ci scrive `page`), quindi
    // ogni chiamata deve avere il suo, altrimenti le due si rubano il numero
    // di pagina a vicenda.
    const buildParams = () => ({
      emission_date__gte: `${this.selectedYear}-01-01`,
      emission_date__lte: `${this.selectedYear}-12-31`,
      is_draft: false,
      is_preventive: false,
      is_deleted: false,
      is_saved_for_preview: false,
    });

    // 1. Create the observables (but don't subscribe yet).
    // getAllInvoices/getAllCorrispettiviReceipts avviano la richiesta subito e
    // possono lanciare in modo sincrono (MissingTokenError da getAuthHeaders):
    // senza try/catch l'eccezione uscirebbe da loadInvoiceData lasciando lo
    // spinner acceso per sempre.
    let invoices$: Observable<{ results: InvoiceInterface[]; failed: boolean }>;
    let corrispettivi$: Observable<{
      results: CorrispettiviReceiptsInterface[];
      failed: boolean;
    }>;
    try {
      invoices$ = this.completedPage(
        this.invoiceApi.getAllInvoices<InvoiceInterface>(
          organization_id,
          buildParams(),
        ),
      );
      corrispettivi$ = this.completedPage(
        this.Corrispettiviservice.getAllCorrispettiviReceipts(
          organization_id,
          buildParams(),
        ),
      );
    } catch (err) {
      console.error('bar-plot: richieste non avviate', err);
      this.errorMessage = 'Errore nel caricamento dei dati.';
      this.isLoading = false;
      this.cdr.detectChanges();
      return;
    }

    // 2. Use forkJoin to wait for BOTH to finish. Ogni sorgente gestisce il
    // proprio errore (vedi completedPage), quindi il fallimento di una sola
    // delle due non annulla piu' l'intero grafico.
    this.dataSubscription = forkJoin({
      invoices: invoices$,
      corrispettivi: corrispettivi$,
    }).subscribe({
      next: ({ invoices, corrispettivi }) => {
        if (invoices.failed && corrispettivi.failed) {
          this.errorMessage = 'Errore nel caricamento dei dati.';
        } else {
          if (invoices.failed) {
            this.warningMessage =
              'Non è stato possibile caricare le fatture: il grafico mostra solo i corrispettivi.';
          } else if (corrispettivi.failed) {
            this.warningMessage =
              'Non è stato possibile caricare i corrispettivi: il grafico mostra solo le fatture.';
          }

          // 3. Process the data now that you are 100% sure both are ready
          this.processInvoiceData(invoices.results, corrispettivi.results);
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('bar-plot: caricamento dati non riuscito', err);
        this.errorMessage = 'Errore nel caricamento dei dati.';
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  // Normalizza una sorgente getRecursivePage() per forkJoin:
  // - filter(completed): il Subject emette una volta per pagina, e take(1) da
  //   solo prendeva la PRIMA emissione — cioè solo la prima pagina, scartando
  //   in silenzio i dati delle successive su organizzazioni con molti documenti.
  // - take(1): il Subject non chiama mai complete(), e forkJoin emette solo
  //   quando tutte le sorgenti completano. Senza questo resterebbe appeso.
  // - catchError: isola il fallimento alla singola sorgente.
  private completedPage<T>(
    source: Observable<RecursiveInterface<T>>,
  ): Observable<{ results: T[]; failed: boolean }> {
    return source.pipe(
      // `each` e non solo `first`: su piu' pagine ogni emissione riarma il
      // contatore, così un elenco lungo ma sano non va in timeout.
      timeout({ first: REQUEST_TIMEOUT_MS, each: REQUEST_TIMEOUT_MS }),
      filter((page) => page.completed),
      take(1),
      map((page) => ({ results: page.results ?? [], failed: false })),
      catchError((err) => {
        console.error('bar-plot: sorgente dati non disponibile', err);
        return of({ results: [] as T[], failed: true });
      }),
    );
  }

  ngOnDestroy(): void {
    this.dataSubscription?.unsubscribe();
  }

  getCssVariable(name: string): string {
    return getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
  }

  // Stondatura solo sul lato "libero" della barra: il lato appoggiato all'asse
  // resta a spigolo. I nomi degli angoli in Chart.js sono assoluti (topLeft e'
  // sempre in alto sul canvas), non relativi alla base: per una barra negativa
  // — possibile, le note di credito TD04 sottraggono dal totale del trimestre —
  // vanno quindi invertiti, altrimenti la stondatura finirebbe sull'asse.
  private barCornerRadius = (context: { raw?: unknown }) => {
    const value = Number(context.raw) || 0;
    const r = 12;
    return value < 0
      ? { topLeft: 0, topRight: 0, bottomLeft: r, bottomRight: r }
      : { topLeft: r, topRight: r, bottomLeft: 0, bottomRight: 0 };
  };

  private processInvoiceData(
    invoices: InvoiceInterface[],
    corrispettivi: CorrispettiviReceiptsInterface[],
  ): void {
    // console.log('!invoices', !invoices);
    // console.log('!corrispettivi', !corrispettivi);
    // console.log(
    //   'invoices.length + corrispettivi.length',
    //   invoices.length + corrispettivi.length,
    // );

    if (
      (!invoices && !corrispettivi) ||
      invoices.length + corrispettivi.length === 0
    ) {
      this.errorMessage = "Nessun documento per l'anno selezionato.";
      return;
    }

    // Group invoices by quarter and calculate totals
    const quarters = {
      Q1: { total: 0, count: 0 },
      Q2: { total: 0, count: 0 },
      Q3: { total: 0, count: 0 },
      Q4: { total: 0, count: 0 },
    };

    invoices.forEach((invoice) => {
      const invoiceDate = new Date(invoice.emission_date);
      const month = invoiceDate.getMonth();
      const quarter = Math.floor(month / 3) + 1;
      const quarterKey = `Q${quarter}` as keyof typeof quarters;

      if (quarters[quarterKey]) {
        if (invoice.document_type === 'TD04') {
          quarters[quarterKey].total -= Number(invoice.amount) || 0; // subtract if Nota di credito
        } else {
          quarters[quarterKey].total += Number(invoice.amount) || 0;
        }

        quarters[quarterKey].count++;
      }
    });
    corrispettivi.forEach((corrispettivo) => {
      const invoiceDate = new Date(corrispettivo.transmission_date);
      const month = invoiceDate.getMonth();
      const quarter = Math.floor(month / 3) + 1;
      const quarterKey = `Q${quarter}` as keyof typeof quarters;

      if (quarters[quarterKey]) {
        quarters[quarterKey].total += Number(corrispettivo.amount) || 0;
        quarters[quarterKey].count++;
      }
    });

    // Prepare chart data
    this.barChartData = {
      labels: ['Q1', 'Q2', 'Q3', 'Q4'],
      datasets: [
        {
          label: 'Somma Fatturato',
          data: [
            quarters.Q1.total,
            quarters.Q2.total,
            quarters.Q3.total,
            quarters.Q4.total,
          ],
          backgroundColor: this.getCssVariable('--info-color'),
          hoverBackgroundColor: this.getCssVariable('--info-2-color'),
          // Contorno nero 2px come i bordi delle card. borderSkipped: false
          // disegna anche il lato appoggiato all'asse, che Chart.js per default
          // ('start') salta: senza, la barra risulterebbe aperta in basso.
          borderColor: '#000000',
          hoverBorderColor: '#000000',
          borderWidth: 2,
          borderSkipped: false,
          borderRadius: this.barCornerRadius,
          barPercentage: 0.8,
        },
        {
          label: 'Numero di documenti',
          data: [
            quarters.Q1.count,
            quarters.Q2.count,
            quarters.Q3.count,
            quarters.Q4.count,
          ],
          backgroundColor: this.getCssVariable('--warn-color'),
          hoverBackgroundColor: this.getCssVariable('--warn-2-color'),
          borderColor: '#000000',
          hoverBorderColor: '#000000',
          borderWidth: 2,
          borderSkipped: false,
          borderRadius: this.barCornerRadius,
          barPercentage: 0.8,
          yAxisID: 'y1',
        },
      ],
    };

    // Add second y-axis for count
    this.barChartOptions = {
      ...this.barChartOptions,
      scales: {
        ...this.barChartOptions?.scales,
        y: {
          ...this.barChartOptions?.scales?.['y'],
          title: {
            display: true,
            text: 'Ammontare (€)',
            color: '#616161',
          },
        },
        y1: {
          position: 'right',
          title: {
            display: true,
            text: 'Numero di documenti',
            color: '#616161',
          },
          grid: {
            drawOnChartArea: false,
          },
        },
      },
    };
  }

  refreshData(): void {
    this.loadInvoiceData();

    this.cdr.detectChanges();
  }
}
